import { useIntl, FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef, useScreenRenderContext } from '@micromag/core/contexts';
import { useTrackScreenEvent, useDimensionObserver } from '@micromag/core/hooks';
import { isTextFilled, isHeaderFilled, isFooterFilled, isImageFilled, getFooterProps } from '@micromag/core/utils';
import Author from '@micromag/element-author';
import Background from '@micromag/element-background';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout from '@micromag/element-layout';
import Scroll from '@micromag/element-scroll';
import Text from '@micromag/element-text';
import Visual from '@micromag/element-visual';

var styles = {"container":"micromag-screen-article-container","background":"micromag-screen-article-background","emptySurtitle":"micromag-screen-article-emptySurtitle","emptyDate":"micromag-screen-article-emptyDate","emptyTitle":"micromag-screen-article-emptyTitle","emptyAuthor":"micromag-screen-article-emptyAuthor","emptySubtitle":"micromag-screen-article-emptySubtitle","emptyText":"micromag-screen-article-emptyText","content":"micromag-screen-article-content","layout":"micromag-screen-article-layout","main":"micromag-screen-article-main","hasText":"micromag-screen-article-hasText","visualContainer":"micromag-screen-article-visualContainer","surtitle":"micromag-screen-article-surtitle","subtitle":"micromag-screen-article-subtitle","date":"micromag-screen-article-date","title":"micromag-screen-article-title","author":"micromag-screen-article-author","text":"micromag-screen-article-text","topContent":"micromag-screen-article-topContent","footer":"micromag-screen-article-footer","disabled":"micromag-screen-article-disabled","isPlaceholder":"micromag-screen-article-isPlaceholder"};

var propTypes = {
  layout: PropTypes.oneOf(['normal']),
  image: PropTypes$1.imageMedia,
  title: PropTypes$1.text,
  surtitle: PropTypes$1.text,
  date: PropTypes.string,
  author: PropTypes.shape({}),
  // author: MicromagPropTypes.text,
  text: PropTypes$1.text,
  subtitle: PropTypes$1.text,
  spacing: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  preload: PropTypes.bool,
  type: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'normal',
  image: null,
  title: null,
  surtitle: null,
  date: null,
  author: null,
  subtitle: null,
  text: null,
  spacing: 20,
  background: null,
  header: null,
  footer: null,
  current: true,
  preload: true,
  type: null,
  className: null
};
var ArticleScreen = function ArticleScreen(_ref) {
  var image = _ref.image,
    title = _ref.title,
    surtitle = _ref.surtitle,
    date = _ref.date,
    author = _ref.author,
    subtitle = _ref.subtitle,
    text = _ref.text,
    spacing = _ref.spacing,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    preload = _ref.preload,
    type = _ref.type,
    className = _ref.className;
  var intl = useIntl();
  var trackScreenEvent = useTrackScreenEvent(type);
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var _useDimensionObserver = useDimensionObserver(),
    imageCntRef = _useDimensionObserver.ref,
    imageHeight = _useDimensionObserver.height;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || preload;
  var ready = true;
  var transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;
  var scrollingDisabled = !isEdit && transitionDisabled || !current;
  var _ref2 = author || {},
    _ref2$name = _ref2.name,
    authorName = _ref2$name === void 0 ? null : _ref2$name,
    _ref2$image = _ref2.image,
    authorImage = _ref2$image === void 0 ? null : _ref2$image,
    authorCollabs = _ref2.collaborator;
  var hasText = isTextFilled(text);
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var hasTitle = isTextFilled(title);
  var hasSurtitle = isTextFilled(surtitle);
  var hasSubtitle = isTextFilled(subtitle);
  var hasSimpleAuthor = isTextFilled(author); // legacy
  var hasAuthor = isTextFilled(authorName) || isImageFilled(authorImage) || isTextFilled(authorCollabs);
  var hasImage = isImageFilled(image);
  var hasDate = isTextFilled(date);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var mediaHeight = useMemo(function () {
    if (!hasImage) {
      return 0;
    }
    var _ref3 = image || {},
      _ref3$metadata = _ref3.metadata,
      metadata = _ref3$metadata === void 0 ? {} : _ref3$metadata;
    var _ref4 = metadata || {},
      _ref4$width = _ref4.width,
      initialWidth = _ref4$width === void 0 ? 0 : _ref4$width,
      _ref4$height = _ref4.height,
      initialHeight = _ref4$height === void 0 ? 0 : _ref4$height;
    return width * initialHeight / initialWidth;
  }, [image]);
  var partialDate = hasDate ? date.body || null : null;
  var finalDate = useMemo(function () {
    return partialDate !== null ? "<p>".concat(intl.formatDate(dayjs(partialDate).toDate(), {
      year: 'numeric',
      month: 'long',
      day: '2-digit'
    }), "</p>") : null;
  }, [partialDate]);
  var imageElement = /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "image",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "ebsPd4",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    }),
    emptyClassName: styles.emptyText,
    isEmpty: !hasImage
  }, /*#__PURE__*/React.createElement("div", {
    ref: imageCntRef,
    className: styles.visualContainer
  }, hasImage ? /*#__PURE__*/React.createElement(Visual, {
    media: image
    // width={width - spacing * 2} // in layout flow
    ,
    width: width,
    height: mediaHeight,
    shouldLoad: mediaShouldLoad,
    resolution: resolution,
    className: styles.visual
  }) : null));
  var titleElement = /*#__PURE__*/React.createElement(ScreenElement, {
    key: "title",
    placeholder: "title",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "2ZOPe+",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    emptyClassName: styles.emptyTitle,
    isEmpty: !hasTitle
  }, hasTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({
    className: styles.title
  }, title)) : null);
  var surtitleElement = /*#__PURE__*/React.createElement(ScreenElement, {
    key: "surtitle",
    placeholder: "line",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "vdWMTA",
      defaultMessage: [{
        "type": 0,
        "value": "Surtitle"
      }]
    }),
    emptyClassName: styles.emptySurtitle,
    isEmpty: !hasSurtitle
  }, hasSurtitle ? /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.surtitle
  }, surtitle)) : null);
  var subtitleElement = /*#__PURE__*/React.createElement(ScreenElement, {
    key: "subtitle",
    placeholder: "line",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "d60tSn",
      defaultMessage: [{
        "type": 0,
        "value": "Subtitle"
      }]
    }),
    emptyClassName: styles.emptySubtitle,
    isEmpty: !hasSubtitle
  }, hasSubtitle ? /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.subtitle
  }, subtitle)) : null);
  var dateElement = /*#__PURE__*/React.createElement(ScreenElement, {
    key: "date",
    placeholder: "line",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "/MsgWe",
      defaultMessage: [{
        "type": 0,
        "value": "Date"
      }]
    }),
    emptyClassName: styles.emptyDate,
    isEmpty: !hasDate
  }, hasDate ? /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.date
  }, date, {
    body: finalDate
  })) : null);
  var authorElement = /*#__PURE__*/React.createElement(ScreenElement, {
    key: "author",
    placeholder: "line",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "xYqGzM",
      defaultMessage: [{
        "type": 0,
        "value": "Author"
      }]
    }),
    emptyClassName: styles.emptyAuthor,
    isEmpty: !hasAuthor && !hasSimpleAuthor
  }, hasSimpleAuthor ? /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.author
  }, author)) : null, hasAuthor ? /*#__PURE__*/React.createElement(Author, {
    author: author
  }) : null);
  var contentElement = /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "text",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "z4CiV1",
      defaultMessage: [{
        "type": 0,
        "value": "Text"
      }]
    }),
    emptyClassName: styles.emptyText,
    isEmpty: !hasText
  }, hasText ? /*#__PURE__*/React.createElement(Text, Object.assign({
    className: styles.text
  }, text)) : null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    scrolledBottom = _useState2[0],
    setScrolledBottom = _useState2[1];
  var onScrolledBottom = useCallback(function (_ref5) {
    var initial = _ref5.initial;
    if (initial) {
      trackScreenEvent('scroll', 'Screen');
    }
    setScrolledBottom(true);
  }, [trackScreenEvent]);
  var onScrolledNotBottom = useCallback(function () {
    setScrolledBottom(false);
  }, [setScrolledBottom]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder)]),
    "data-screen-ready": ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Scroll, {
    disabled: scrollingDisabled,
    onScrolledBottom: onScrolledBottom,
    onScrolledNotBottom: onScrolledNotBottom,
    withShadow: true
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: hasHeader ? spacing / 2 + (!isPreview ? viewerTopHeight : 0) : spacing / 2 + imageHeight,
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + spacing / 2
    } : null
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    key: "header",
    style: {
      height: hasImage && imageHeight > 0 ? 0 : 'auto',
      paddingBottom: imageHeight > 0 ? imageHeight - viewerTopHeight : spacing
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.main, _defineProperty({}, styles.hasText, hasText && hasAuthor)])
  }, imageElement, /*#__PURE__*/React.createElement("div", {
    className: styles.topContent
  }, surtitleElement, dateElement), titleElement, authorElement, subtitleElement), contentElement, !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.footer, _defineProperty({}, styles.disabled, !scrolledBottom)]),
    style: {
      paddingLeft: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingRight: Math.max(viewerBottomSidesWidth - spacing, 0),
      paddingTop: spacing
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null))), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
ArticleScreen.propTypes = propTypes;
ArticleScreen.defaultProps = defaultProps;

// import * as transforms from './transforms/index';

var definition = [{
  id: 'article',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "fIawTr",
      defaultMessage: [{
        "type": 0,
        "value": "Text"
      }]
    }),
    order: 2
  },
  title: defineMessage({
    id: "ouMwWq",
    defaultMessage: [{
      "type": 0,
      "value": "Article"
    }]
  }),
  component: ArticleScreen,
  layouts: ['normal'],
  // transforms,
  fields: [{
    name: 'image',
    type: 'visual',
    label: defineMessage({
      id: "xsipID",
      defaultMessage: [{
        "type": 0,
        "value": "Image"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    inline: true,
    theme: {
      textStyle: 'header2'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'surtitle',
    type: 'text-element',
    label: defineMessage({
      id: "YD9M2p",
      defaultMessage: [{
        "type": 0,
        "value": "Overtitle"
      }]
    })
  }, {
    name: 'date',
    type: 'date-element',
    label: defineMessage({
      id: "9AP246",
      defaultMessage: [{
        "type": 0,
        "value": "Date"
      }]
    })
  }, {
    name: 'author',
    type: 'author-element',
    label: defineMessage({
      id: "73hxYw",
      defaultMessage: [{
        "type": 0,
        "value": "Author"
      }]
    })
  }, {
    name: 'subtitle',
    type: 'text-element',
    label: defineMessage({
      id: "EcZYpr",
      defaultMessage: [{
        "type": 0,
        "value": "Subtitle"
      }]
    })
  }, {
    name: 'text',
    type: 'text-modal',
    label: defineMessage({
      id: "4E2gbX",
      defaultMessage: [{
        "type": 0,
        "value": "Text"
      }]
    })
  },
  // TODO: think about how to implement this wthout injecting the styles
  // {
  //     type: 'fields',
  //     isList: true,
  //     label: defineMessage({
  //         defaultMessage: 'Styles',
  //         description: 'Field section label',
  //     }),
  //     fields: [
  //         {
  //             name: 'textTitleStyle',
  //             type: 'text-style-form',
  //             label: defineMessage({
  //                 defaultMessage: 'Text title style',
  //                 description: 'Field label',
  //             }),
  //         },
  //         {
  //             name: 'textQuoteStyle',
  //             type: 'text-style-form',
  //             label: defineMessage({
  //                 defaultMessage: 'Text quote style',
  //                 description: 'Field label',
  //             }),
  //         },
  //     ],
  // },
  {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
}];

export { ArticleScreen, definition as default };
